import {withPageErrorComponent} from 'HoC/withPageErrorComponent';
import {GroupFilterContext} from 'context/groupFilterContext';
import {FormProvider} from 'hooks/custom-react-hook-form';
import {useTranslation} from 'hooks/translations/useCustomTranslation';
import {useFilterAndPrices} from 'hooks/useFilterAndPrices';
import {useGroupFilterForm} from 'hooks/useGroupFilterForm';
import {serverSideTranslations} from 'next-i18next/serverSideTranslations';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import {InferGetServerSidePropsType} from 'next/types';
import {useCallback, useState} from 'react';

// Imports precompiled bundle
import {useMediaQuery} from '@mui/material';

import {getApiV1Groups, getApiV1GroupsMapping} from 'lib/api/backend';
import {GetApiV1GroupsMapping200, Group} from 'lib/api/backend.schemas';

import {DefaultPagePropsProvider, TDefaultProps} from 'utils/dataFetching/DefaultPagePropsProvider';
import getDefaultPageProps from 'utils/dataFetching/getDefaultPageProps';
import {translationFiles} from 'utils/translations/constants';
import {redirectToLocalePath} from 'utils/translations/redirectLocale';
import {setCurrentLocale} from 'utils/translations/setCurrentLocale';

import {Box, Grid} from 'components/basic-components';
import NoResults from 'components/basic-components/NoResults/NoResults';
import ContentWrapper from 'components/layout-components/ContentWrapper/ContentWrapper';
import Layout from 'components/layout-components/Layout/Layout';
import BPRModal from 'components/section-components/bpr/BPRModal/BPRModal';
import CarFilter from 'components/section-components/reservation/step1/CarFilter/CarFilter';
import ResultBar from 'components/section-components/reservation/step1/ResultBar/ResultBar';
import ResultCard from 'components/section-components/reservation/step1/ResultCard/ResultCard';

import {GroupTypesMappingContext} from '../context/GroupTypesMappingContext';

export async function getStaticProps(ctx): Promise<any> {
  const {locale, locales} = ctx;
  const redirect = redirectToLocalePath({ctx, page_locale, path_mapping});
  if (redirect) return redirect;
  setCurrentLocale(locale);
  const categoryTypeMapping = await getApiV1GroupsMapping();
  // console.log(categoryTypeMapping);
  const groups = await getApiV1Groups({sortResult: true, withImages: true});
  const groupData = [];
  Object.values(groups).forEach(category => {
    Object.values(category).forEach((type: any) => {
      type.forEach(group => groupData.push(group));
    });
  });
  return {
    props: {
      defaultProps: await getDefaultPageProps({locale, locales, seoPath: 'tarife'}),
      category: locale === 'de' ? 'pkw' : 'car',
      categoryTypeMapping,
      ...(await serverSideTranslations(locale, translationFiles)),
      groups: groupData,
    }, // will be passed to the page component as props
    revalidate: 60,
  };
}

// export const getServerSideProps = wrapGetServerSidePropsWithTryCatch(getServerSidePropsInner);

// will be able to inferServerSide PRops with next 12.3.2
// https://github.com/vercel/next.js/pull/40635

const DynamicPageInner = ({category, groups: groupsData}: {category: string; groups: Group[]}) => {
  const {t} = useTranslation(['reservation', 'bpr']);

  const [selectedGroup, setSelectedGroup] = useState<Group>();
  const [selectedVehicle, setSelectedVehicle] = useState<string>();
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const {formMethods: groupFilterFormMethods, defaultValues} = useGroupFilterForm({category});
  const {getValues, setValue} = groupFilterFormMethods;
  const {category: currentCategory} = getValues();
  const setCurrentCategory = useCallback(cat => setValue('category', cat), [setValue]);

  const UseFilterAndPricesReturn = useFilterAndPrices({
    formMethods: groupFilterFormMethods,
    defaultValues,
    category,
    groupsData,
    setCurrentCategory,
  });
  const {formMethods, visibleGroupUIDs, sortedGroups} = UseFilterAndPricesReturn;
  const displayFilter = useMediaQuery('(min-width:1000px)');
  /** //////////////////////////////////////// */
  return (
    <GroupFilterContext.Provider value={UseFilterAndPricesReturn}>
      <div style={{zIndex: 1}}>
        <Head>
          {/* {metaTags} */}
          <title>{t('vehicleSelection')}</title>
        </Head>

        <ContentWrapper style={{overflow: 'hidden'}}>
          <FormProvider {...formMethods}>
            <BPRModal
              modalOpen={modalOpen}
              presetGroup={selectedGroup?.id}
              minDate={selectedGroup?.bookableFrom}
              maxDate={selectedGroup?.bookableTo}
              presetCategory={selectedGroup?.category}
              preferredVehicle={selectedVehicle ? selectedVehicle : null}
              presetType={selectedGroup?.type}
              presetVip={selectedGroup?.vip}
              setModalOpen={setModalOpen}
              hideOneWay={!selectedGroup?.oneWay}
            />
            <Grid
              container
              sx={{paddingTop: '50px', paddingBottom: '50px', margin: '0 2%'}}
              columnSpacing={{xxs: 0, xl: '2%'}}
            >
              <Grid hidden={!displayFilter} xl={3}>
                <CarFilter />
              </Grid>
              <Grid xxs={12} xl={9}>
                <ResultBar />
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    rowGap: '20px',
                    paddingTop: '50px',
                  }}
                >
                  {visibleGroupUIDs.length > 0 ? (
                    <>
                      {[...groupsData]
                        .filter(group => group.category === currentCategory)
                        ?.map(group => {
                          return (
                            <ResultCard
                              clickHandler={(group, selectedVehicle) => {
                                setSelectedGroup(group),
                                  setSelectedVehicle(selectedVehicle),
                                  setModalOpen(true);
                              }}
                              key={`${group.id}-${group.type}-${group.category}`}
                              isVisible={
                                group.category === currentCategory &&
                                visibleGroupUIDs.includes(`${group.id}-${group.type}`)
                              }
                              group={group}
                              sort={sortedGroups.indexOf(`${group.id}-${group.type}`)}
                            />
                          );
                        })}
                    </>
                  ) : (
                    <NoResults reponseMessage={t('noResults')} />
                  )}
                </Box>
              </Grid>
            </Grid>
          </FormProvider>
        </ContentWrapper>
      </div>
    </GroupFilterContext.Provider>
  );
};
const DynamicPage: InferGetServerSidePropsType<any> = ({
  defaultProps,
  category,
  groups: groupsData,
  categoryTypeMapping,
}: {
  defaultProps: TDefaultProps;
  category: string;
  categoryTypeMapping: GetApiV1GroupsMapping200;
  groups: Group[];
}) => {
  return (
    <DefaultPagePropsProvider defaultProps={defaultProps}>
      <GroupTypesMappingContext.Provider value={categoryTypeMapping}>
        <Layout
          translatedSlugs={[
            {slug: 'tarife', locale: 'de'},
            {slug: 'rates', locale: 'en'},
          ]}
        >
          <DynamicPageInner category={category} groups={groupsData} />
        </Layout>
      </GroupTypesMappingContext.Provider>
    </DefaultPagePropsProvider>
  );
};
export default dynamic(() => Promise.resolve(withPageErrorComponent(DynamicPage)), {ssr: false});

export const path_mapping = {de: 'tarife', en: 'rates'};
export const page_locale = 'de';
